import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { backupConfigAPI } from '../api/backupConfig';
import { backupLogAPI } from '../api/backupLog';
import { toast } from 'react-toastify';
import './InstallationDetails.css';

const InstallationDetails = () => {
  const { uniqueId } = useParams();
  const navigate = useNavigate();
  const [configs, setConfigs] = useState([]);
  const [recentLogs, setRecentLogs] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [uniqueId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const configResponse = await backupConfigAPI.getByInstallation(uniqueId);
      setConfigs(configResponse.data);

      // Fetch recent log for each config
      const logsPromises = configResponse.data.map(config =>
        backupLogAPI.getLogsByConfig(config._id, 1, 1)
          .then(res => ({ configId: config._id, log: res.data[0] }))
          .catch(() => ({ configId: config._id, log: null }))
      );

      const logsResults = await Promise.all(logsPromises);
      const logsMap = {};
      logsResults.forEach(({ configId, log }) => {
        logsMap[configId] = log;
      });
      setRecentLogs(logsMap);
    } catch (error) {
      toast.error('Failed to fetch backup configurations');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (configId, name) => {
    if (!window.confirm(`Delete backup configuration "${name}"?`)) {
      return;
    }

    try {
      await backupConfigAPI.delete(configId);
      toast.success('Backup configuration deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete configuration');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      success: { icon: '✅', text: 'Success', class: 'success' },
      failed: { icon: '❌', text: 'Failed', class: 'failed' },
      in_progress: { icon: '⏳', text: 'Running', class: 'in-progress' },
      initiated: { icon: '🔄', text: 'Starting', class: 'initiated' },
      partial_success: { icon: '⚠️', text: 'Partial', class: 'partial' },
    };
    return badges[status] || { icon: '❓', text: status, class: 'unknown' };
  };

  const formatTimeAgo = (date) => {
    if (!date) return 'Never';
    const seconds = Math.floor((new Date() - new Date(date)) / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    return `${Math.floor(seconds / 86400)}d ago`;
  };

  if (loading) {
    return <div className="loading">Loading backup configurations...</div>;
  }

  return (
    <div className="installation-details-page">
      <div className="page-header">
        <div>
          <button onClick={() => navigate('/installations')} className="btn-back">
            ← Back
          </button>
          <h1>Backup Configurations</h1>
        </div>
        <div className="header-actions">
          <Link
            to={`/installations/${uniqueId}/logs`}
            className="btn btn-secondary"
          >
            📊 View All Logs
          </Link>
          <Link
            to={`/installations/${uniqueId}/backup-config/create`}
            className="btn btn-primary"
          >
            + New Backup Config
          </Link>
        </div>
      </div>

      {configs.length === 0 ? (
        <div className="empty-state">
          <p>No backup configurations found</p>
          <Link
            to={`/installations/${uniqueId}/backup-config/create`}
            className="btn btn-primary"
          >
            Create First Configuration
          </Link>
        </div>
      ) : (
        <div className="configs-list">
          {configs.map((config) => {
            const recentLog = recentLogs[config._id];
            const statusBadge = recentLog ? getStatusBadge(recentLog.status) : null;

            return (
              <div key={config._id} className="config-card">
                <div className="config-header">
                  <div>
                    <h3>{config.configName}</h3>
                    {recentLog && (
                      <div className="last-backup-info">
                        <span className={`mini-status ${statusBadge.class}`}>
                          {statusBadge.icon} {statusBadge.text}
                        </span>
                        <span className="time-ago">
                          {formatTimeAgo(recentLog.startTime)}
                        </span>
                      </div>
                    )}
                  </div>
                  <span className={`status ${config.isActive ? 'active' : 'inactive'}`}>
                    {config.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>

                <div className="config-body">
                  {config.description && (
                    <p className="description">{config.description}</p>
                  )}

                  <div className="config-quick-info">
                    <div className="info-item">
                      <span className="info-label">Database:</span>
                      <span className="info-value">{config.sqlServerConfig.database}</span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">SFTP Servers:</span>
                      <span className="info-value">
                        {config.sftpServers.filter(s => s.isActive).length} active
                      </span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Schedules:</span>
                      <span className="info-value">
                        {config.schedules.filter(s => s.isActive).length} active
                      </span>
                    </div>
                  </div>

                  {recentLog && (
                    <div className="recent-backup-summary">
                      <h4>Last Backup Summary</h4>
                      <div className="summary-grid">
                        {recentLog.duration && (
                          <div className="summary-item">
                            <span>Duration:</span>
                            <strong>{Math.floor(recentLog.duration / 60)}m</strong>
                          </div>
                        )}
                        {recentLog.backupFile?.sizeBytes && (
                          <div className="summary-item">
                            <span>Size:</span>
                            <strong>
                              {(recentLog.backupFile.sizeBytes / 1073741824).toFixed(2)} GB
                            </strong>
                          </div>
                        )}
                        {recentLog.sftpResults?.length > 0 && (
                          <div className="summary-item">
                            <span>SFTP:</span>
                            <strong>
                              {recentLog.sftpResults.filter(r => r.status === 'success').length}/
                              {recentLog.sftpResults.length}
                            </strong>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <div className="config-actions">
                  <Link
                    to={`/backup-config/${config._id}/logs`}
                    className="btn btn-secondary"
                  >
                    📋 Logs
                  </Link>
                  <Link
                    to={`/backup-config/${config._id}/edit`}
                    className="btn btn-secondary"
                  >
                    ✏️ Edit
                  </Link>
                  <button
                    onClick={() => handleDelete(config._id, config.configName)}
                    className="btn btn-danger"
                  >
                    🗑️ Delete
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default InstallationDetails;